import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Calendar, Workflow,
  CreditCard, Settings, Search, Inbox as InboxIcon,
  GitBranch, CheckSquare, ListFilter, Link as LinkIcon,
  Mail as MailIcon, Megaphone, ShoppingBag, Package, Receipt,
  TicketPercent, Globe, LineChart, BarChart3, FolderOpen, Headset,
  Sun, Moon, LayoutGrid, Share2, UsersRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toggleTheme, useTheme } from "@/lib/theme";
import { api } from "@/lib/api";
import type { Contact } from "@/lib/types";
import { Avatar } from "@/components/ui/Avatar";
import { fullName } from "@/lib/format";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { useStoreEnabled } from "@/lib/store-enabled";

export interface SubItem {
  to: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}
export interface NavSection {
  /** Path used as the section's "default" page (clicking the top-level
   *  label navigates here). For single-page sections, this is the page. */
  defaultTo: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** All paths that "belong" to this section. Used for active-tab matching. */
  paths: string[];
  /** Sub-pages — when present, a sub-nav row renders below the top nav. */
  subnav?: SubItem[];
}

/**
 * The full app IA. Order matters — sections render left-to-right.
 *
 * Sub-pages are listed in the order they appear in the secondary nav. The
 * first sub-page is the section's default landing page when the user
 * clicks the top-level tab.
 */
export const SECTIONS: NavSection[] = [
  {
    defaultTo: "/",
    label: "Home",
    icon: LayoutDashboard,
    paths: ["/"],
  },
  {
    defaultTo: "/inbox",
    label: "Inbox",
    icon: InboxIcon,
    paths: ["/inbox"],
  },
  {
    defaultTo: "/mail",
    label: "Mail",
    icon: MailIcon,
    paths: ["/mail", "/campaigns"],
    // "Mail" (one-to-one email threads) and "Campaigns" (one email to many
    // people). The top-level Inbox is every channel per contact; naming the
    // email thread list "Inbox" too made two Inboxes, which confused people
    // setting up newsletters (2026-10-15).
    subnav: [
      { to: "/mail",      label: "Mail",      icon: MailIcon },
      { to: "/campaigns", label: "Campaigns", icon: Megaphone },
    ],
  },
  {
    defaultTo: "/contacts",
    label: "Contacts",
    icon: Users,
    paths: ["/contacts", "/pipelines", "/tasks", "/lists", "/dialer", "/team"],
    subnav: [
      { to: "/contacts",   label: "Contacts",     icon: Users },
      { to: "/pipelines",  label: "Pipelines",    icon: GitBranch },
      { to: "/tasks",      label: "Tasks",        icon: CheckSquare },
      { to: "/lists",      label: "Smart Lists",  icon: ListFilter },
      { to: "/team",       label: "Team",         icon: UsersRound },
      { to: "/dialer",     label: "Power Dialer", icon: Headset },
    ],
  },
  {
    defaultTo: "/calendar",
    label: "Calendar",
    icon: Calendar,
    paths: ["/calendar", "/scheduling-links"],
    subnav: [
      { to: "/calendar",          label: "Calendar",      icon: Calendar },
      { to: "/scheduling-links",  label: "Booking links", icon: LinkIcon },
    ],
  },
  {
    defaultTo: "/social",
    label: "Social",
    icon: Share2,
    paths: ["/social", "/instagram"],
    subnav: [
      { to: "/social",          label: "Feed preview", icon: LayoutGrid },
      { to: "/social/calendar", label: "Calendar",     icon: Calendar },
    ],
  },
  {
    defaultTo: "/automations",
    label: "Automations",
    icon: Workflow,
    paths: ["/automations"],
  },
  {
    defaultTo: "/store",
    label: "Store",
    icon: ShoppingBag,
    paths: ["/store"],
    subnav: [
      { to: "/store",             label: "Overview",    icon: LayoutDashboard },
      { to: "/store/products",    label: "Products",    icon: Package },
      { to: "/store/collections", label: "Collections", icon: FolderOpen },
      { to: "/store/orders",      label: "Orders",      icon: Receipt },
      { to: "/store/customers", label: "Customers", icon: Users },
      { to: "/store/discounts", label: "Discounts", icon: TicketPercent },
      { to: "/store/site",      label: "Site",      icon: Globe },
      { to: "/store/settings",  label: "Settings",  icon: Settings },
    ],
  },
  {
    defaultTo: "/affiliates",
    label: "Creators",
    icon: Users,
    paths: ["/affiliates"],
    subnav: [
      { to: "/affiliates", label: "Program", icon: Users },
    ],
  },
  {
    defaultTo: "/traffic",
    label: "Traffic",
    icon: LineChart,
    paths: ["/traffic"],
  },
  {
    defaultTo: "/reports",
    label: "Reports",
    icon: BarChart3,
    paths: ["/reports"],
  },
  {
    defaultTo: "/payments",
    label: "Payments",
    icon: CreditCard,
    paths: ["/payments"],
  },
  // Settings intentionally has no section entry — it renders in the SideNav
  // footer on desktop and as the last chip of the mobile bar.
];

/**
 * The sections the nav should actually SHOW. The store module is dormant on
 * most accounts (they don't sell anything), so its section stays out of the
 * nav until the backend says it's in use — enabled in Store settings, or
 * products/orders exist. The /store routes stay mounted regardless, so a
 * direct link (or the Settings toggle) always works.
 */
export function useNavSections(): NavSection[] {
  const storeEnabled = useStoreEnabled();
  return storeEnabled ? SECTIONS : SECTIONS.filter((s) => s.defaultTo !== "/store");
}

/** Match the section a path belongs to. Returns null for paths that live
 *  outside the section list (e.g. /settings, which has its own links). */
export function matchSection(pathname: string): NavSection | null {
  // Most specific match wins — sort sections by longest matching prefix.
  let best: NavSection | null = null;
  let bestLen = -1;
  for (const s of SECTIONS) {
    for (const p of s.paths) {
      if (p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(p + "/")) {
        if (p.length > bestLen) { best = s; bestLen = p.length; }
      }
    }
  }
  return best;
}

export function TopNav({ workspaceName }: { workspaceName?: string }) {
  const { pathname } = useLocation();
  const activeSection = matchSection(pathname);
  const sections = useNavSections();

  return (
    <header className="sticky top-0 z-30 border-b border-zinc-800/70 bg-zinc-950/95 backdrop-blur supports-backdrop-filter:bg-zinc-950/80">
      <div className="mx-auto flex h-12 max-w-screen-2xl items-center gap-1 px-2 sm:px-4 lg:px-6">
        {/* Primary tabs — below sm only, as icon chips (evenly distributed
            via flex-1 — no horizontal scroll). At sm+ the SideNav
            carries the primary navigation and this row becomes a toolbar:
            sub-nav pills for the active section + search + notifications. */}
        <nav className="flex flex-1 items-center gap-0.5 sm:hidden">
          {sections.map((s) => {
            const isActive = s === activeSection;
            const Icon = s.icon;
            return (
              <NavLink
                key={s.defaultTo}
                to={s.defaultTo}
                end={s.defaultTo === "/"}
                aria-label={s.label}
                className={cn(
                  "group relative inline-flex flex-1 items-center justify-center rounded-lg px-0.5 py-1.5 transition-colors",
                  isActive
                    ? "bg-zinc-800/70 text-zinc-100"
                    : "text-zinc-400 hover:bg-zinc-800/40 hover:text-zinc-100"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <Flyout label={s.label} side="bottom" />
              </NavLink>
            );
          })}
          {/* Settings rides in the chip row below sm; at sm+ it lives in
              the SideNav footer. */}
          <NavLink
            to="/settings"
            aria-label="Settings"
            className={({ isActive }) =>
              cn(
                "group relative inline-flex flex-1 items-center justify-center rounded-lg px-0.5 py-1.5 transition-colors",
                isActive
                  ? "bg-zinc-800/70 text-zinc-100"
                  : "text-zinc-400 hover:bg-zinc-800/40 hover:text-zinc-100"
              )
            }
          >
            <Settings className="h-4 w-4 shrink-0" />
            <Flyout label="Settings" side="bottom" />
          </NavLink>
        </nav>

        {/* Desktop toolbar left half: the active section's sub-pages (the
            sub-row below carries these under sm), or its name when it has
            no sub-pages — the bar always says where you are. */}
        <div className="hidden sm:flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto">
          {activeSection?.subnav && activeSection.subnav.length > 1 ? (
            activeSection.subnav.map((sub) => <SubPill key={sub.to} sub={sub} />)
          ) : (
            <span className="truncate px-1 text-sm font-semibold text-zinc-100">
              {activeSection ? activeSection.label : pathname.startsWith("/settings") ? "Settings" : ""}
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <GlobalSearch />
          <ThemeToggle />
          <NotificationBell />
        </div>
      </div>

      {/* Secondary nav row — below sm only (at sm+ the pills live in the
          toolbar row above). Icon-only chips, evenly distributed (no
          horizontal scroll), hover flyouts carry the labels — even
          Store's eight fit on a 360px phone. */}
      {activeSection?.subnav && activeSection.subnav.length > 1 && (
        <div className="border-t border-zinc-900/60 bg-zinc-950/95 sm:hidden">
          <div className="mx-auto flex h-9 max-w-screen-2xl items-center gap-0.5 px-2">
            {activeSection.subnav.map((sub) => (
              <SubPill key={sub.to} sub={sub} mobile />
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

/** One sub-page pill. Toolbar variant (sm+) shows the label (icon joins at
 *  lg); the mobile sub-row variant is an evenly-stretched icon chip whose
 *  label rides in a hover flyout. */
function SubPill({ sub, mobile }: { sub: SubItem; mobile?: boolean }) {
  const Icon = sub.icon;
  return (
    <NavLink
      to={sub.to}
      end
      aria-label={sub.label}
      className={({ isActive }) =>
        cn(
          "group relative inline-flex items-center justify-center gap-1.5 rounded-md py-1 text-xs font-medium whitespace-nowrap transition-colors",
          mobile ? "flex-1 px-2" : "px-2.5",
          isActive
            ? "bg-rose-500/15 text-rose-200"
            : "text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200"
        )
      }
    >
      {/* Toolbar pills drop their icons until lg — Store's eight pills
          must fit beside the sidebar on an 830px window. */}
      {Icon && <Icon className={mobile ? "h-3.5 w-3.5" : "hidden lg:block h-3 w-3"} />}
      {mobile ? <Flyout label={sub.label} side="bottom" /> : <span>{sub.label}</span>}
    </NavLink>
  );
}

/** Hover label for icon-only nav controls. Parent needs `group relative`.
 *  side="right" flies out beside the collapsed SideNav rail; side="bottom"
 *  drops below the mobile chip bars. Pointer-events-none so it never
 *  swallows the click it's describing. */
export function Flyout({ label, side = "right" }: { label: string; side?: "right" | "bottom" }) {
  return (
    <span
      className={cn(
        "pointer-events-none absolute z-50 whitespace-nowrap rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs font-medium text-zinc-100 opacity-0 shadow-lg shadow-shade/40 transition-opacity duration-100 group-hover:opacity-100",
        side === "right"
          ? "left-full top-1/2 ml-3 -translate-y-1/2"
          : "top-full left-1/2 mt-1.5 -translate-x-1/2"
      )}
    >
      {label}
    </span>
  );
}

/** Dark/light switch. Mirrors the Appearance setting in Settings →
 *  Workspace — both drive the same persisted theme store. */
function ThemeToggle() {
  const theme = useTheme();
  const dark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="group relative inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900/50 text-zinc-400 transition-colors hover:bg-zinc-900 hover:text-zinc-100"
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      <Flyout label={dark ? "Light mode" : "Dark mode"} side="bottom" />
    </button>
  );
}

function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  // Esc closes the popover. (No ⌘K shortcut — visible button is enough.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const h = setTimeout(() => {
      api.get<{ contacts: Contact[] }>("/contacts", { q: q.trim(), limit: 8 })
        .then((d) => setResults(d.contacts))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 150);
    return () => clearTimeout(h);
  }, [q]);

  return (
    <div ref={dropRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="group relative inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900/50 text-zinc-400 transition-colors hover:bg-zinc-900 hover:text-zinc-100"
        aria-label="Search contacts"
      >
        <Search className="h-4 w-4" />
        {!open && <Flyout label="Search contacts" side="bottom" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 max-w-[92vw] rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-shade/40 animate-fade-in z-40">
          <div className="border-b border-zinc-800 p-2">
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search contacts…"
              className="w-full bg-transparent px-2 py-1.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {loading && <p className="p-3 text-xs text-zinc-500">Searching…</p>}
            {!loading && q && results.length === 0 && <p className="p-3 text-xs text-zinc-500">No matches.</p>}
            {!loading && !q && <p className="p-3 text-xs text-zinc-500">Type to search contacts</p>}
            {results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { navigate(`/contacts/${c.id}`, { state: { from: pathname } }); setOpen(false); setQ(""); }}
                className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-zinc-900"
              >
                <Avatar name={fullName(c)} email={c.email} src={c.avatar_url} size="xs" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-zinc-100">{fullName(c)}</p>
                  <p className="truncate text-xs text-zinc-500">{c.email || c.phone}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
